import { useMutation } from '@tanstack/react-query'

import type { IHttpError, IPagination, IResponse } from '../core'
import { ObjectsFactory, Model } from '../core'
import type { IRole } from './role'
import { ICompany, IFile, NotificationChannel } from '@/@data'
import { IBank } from '../bank'

export interface IData {
  metadataKey: string
}

export interface IImportError {
  id: number
  row: number
  first_name: string
  employee_id: string
  check: string
  error: string
  data: IUser
}

export interface IImportErrorDelete {
  id: number
  row: number
  first_name: string
  employee_id: string
  check: string
  data: IUser
}
export interface IImportResponse {
  message: string
  totalProcessed: number
  successCount: number
  errorCount: number
  errors: IImportError[]
  dataDelete: IImportErrorDelete[]
}

export interface IImportResponseDelete {
  userIds: number[]
}

export enum UserStatusEnum {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  BLOCKED = 'blocked',
  WAITING = 'waiting',
  DISABLE = 'disable',
  WAITING_DELETE = 'waiting_delete',
  DELETED = 'deleted',

  // INVITED = 'invited',
  GUEST = 'guest',
  GUEST_WAITING = 'guest_waiting'
}

export enum UserTypeEnum {
  Admin = 'admin',
  User = 'user'
}

export enum RoleCodeEnum {
  Master = 'master',
  SubMaster = 'sub_master',
  CageManage = 'cage_manage',
  FloorManage = 'floor_manage',
  Supervisor = 'supervisor',
  Roller = 'roller',
  Agent = 'agent',
  Player = 'player'
}

export interface IUser {
  eligibility_status?: string
  eligibility_rate?: number
  eligibility_updated_at?: string
  eligibility_updated_by?: string
  id: number
  system_code: string
  first_name: string
  last_name: string
  branch_id: number
  employee_id: string
  email: string
  password: string
  address: string
  agent_id: number
  approved_by: number
  phone: string
  phone_code: string
  avatar: string
  signature: string
  hand_card: string
  country: string
  status: UserStatusEnum
  status_previous?: UserStatusEnum
  token: string
  token_validity_date: Date
  salt: string
  two_fa_secret: string
  two_fa_throttle_time: Date
  is_two_fa_enabled: boolean
  is_pin_code_enabled: boolean
  role: IRole
  role_id: number
  user_type: UserTypeEnum
  channel: NotificationChannel.Email
  otpAuthUrl: string
  created_by: number
  approved_by_user: IUser
  company_id: any
  company: ICompany
  company_name?: string
  pin_code: string
  pin_token: string
  avatar_file: IFile
  created_at: string
  updated_at: string
  create_user: IUser
  birth_date: string
  extra: Record<string, any>
  is_valid_email?: boolean
  is_valid_phone?: boolean
  is_valid_phone_code?: boolean
  is_valid_employee?: boolean
  bank_id: number
  bank_account_number: string
  bank_account_name: string
  working_date: string
  working_type: string
  working_rate: string
  department: string
  bank: IBank
  joined_date: string
  contract_type: string
  identity_card: number
  identity_card_issue_date: string
  identity_card_issue_place: string
  gender: string
  tenure: string
  dependent: string
  employee_name: string
  create_date: string
  user_code: string
  bank_name: string
  request_delete_at: string
  request_id: number
  request_delete: number
}

export interface ICreateUserPayload {
  company: ICompany
  first_name: string
  employee_id: string
  joined_date: string
  contract_type: string
  phone_code: string
  phone: string
  bank_id: number
  bank_account_number: string
  bank_account_name: string
  identity_card: number
  identity_card_issue_date: string
  identity_card_issue_place: string
  gender: string
  address: string
  birth_date: string
  dependent: string
  tenure: string
  company_id: any
  confirm_create?: boolean
}

export interface IImportEmployeePayload {
  company_id: ICompany
  file: File
}

export interface IStaticUserRole {
  count: number
  role_id: number
  role_name: string
  users: IUser[]
}

export interface IChangePermissionUserPayload {
  from: number
  to?: number
  userIds: number[]
}

export interface IMappingPhonePayload {
  company_id: number
  from: number
  to: number
}

export interface IUsersListResponse extends IPagination {
  userList: IUser[]
}

const modelConfig = {
  path: '/users',
  model: 'USER'
}

export class User extends Model {
  static queryKeys = {
    paginate: 'USERS_PAGINATE_QUERY',
    findOne: 'USERS_FIND_ONE_QUERY'
  }

  static objects = ObjectsFactory.factory<IUser>(modelConfig, this.queryKeys)

  static getStaticUserRole() {
    return {
      queryKey: ['STATIC_USER_ROLE_QUERY'],
      queryFn: () => {
        return this.api
          .get<IResponse<IStaticUserRole[]>>({
            url: '/users/inactive'
          })
          .then(r => r.data)
      }
    }
  }

  static getUsersSelectOptions(users: IUser[] | undefined) {
    if (!users) return []

    return users.map(item => ({
      value: item.id.toString(),
      label: `${item.first_name}`,
      status: item.status
    }))
  }

  static createUser(payload: ICreateUserPayload) {
    return this.api.post<IResponse<IUser>>({
      url: '/users',
      data: payload
    })
  }

  static createEmployee(payload: ICreateUserPayload) {
    return this.api.post<IResponse<IUser>>({
      url: '/employee',
      data: payload
    })
  }

  static updateStatus(userIds: number[], status: UserStatusEnum, note?: { message: string }[]) {
    const users = userIds.map((id, index) => ({
      user_id: id,
      ...(note && note[index] ? { note: note[index] } : {})
    }))

    return this.api.post<IResponse<IUser[]>>({
      url: `/users/change-status`,
      data: {
        users,
        status
      }
    })
  }

  static updateUser(id: number, payload: Partial<IUser>) {
    return this.api.put<IResponse<IUser>>({
      url: `/users/${id}`,
      data: payload
    })
  }

  static updateEmployee(id: number, payload: Partial<IUser>) {
    return this.api.put<IResponse<IUser>>({
      url: `/employee/${id}`,
      data: payload
    })
  }

  static importEmployee(payload: any) {
    return this.api.post<IResponse<IImportResponse>>({
      url: `/employee/import`,
      data: payload
    })
  }

  static importEmployeeDelete(payload: any) {
    return this.api.post<IResponse<IImportResponseDelete>>({
      url: `/employee/restore-waiting`,
      data: payload
    })
  }

  static assignAgent(payload: { user_id: number; agent_id: number }) {
    return this.api.post<IResponse<IUser>>({
      url: '/users/assign-agent',
      data: payload
    })
  }

  static promoteUser(id: number) {
    return this.api.put<IResponse<IUser>>({
      url: `/users/promote/${id}`
    })
  }

  static updateAvatar(id: number, avatar: File) {
    return this.api.uploadFile<IResponse<IUser>>(`${modelConfig.path}/${id}/update-avatar`, avatar)
  }

  static changePermissionUser(payload: IChangePermissionUserPayload) {
    return this.api.put<IResponse<IUser>>({
      url: '/users/change-permission',
      data: payload
    })
  }

  static toggleShowPoint(id: number, show: boolean) {
    return this.api.put<IResponse<IUser[]>>({
      url: `/users/point/${id}`,
      data: {
        is_show_point: show
      }
    })
  }

  static resendInviteUser(id: number) {
    return this.api.post<IResponse<IUser>>({
      url: `/users/resend-invite/${id}`
    })
  }

  static exportEmployee(userIds?: number[], filterParams?: Record<string, string | undefined>) {
    const params: Record<string, any> = {}
    
    if (userIds && userIds.length > 0) {
      params.user_ids = userIds
    } else if (filterParams) {
      // Only add filter params if no user_ids are provided
      if (filterParams.company_id) params.company_id = filterParams.company_id
      if (filterParams.status) params.status = filterParams.status
      if (filterParams.created_from) params.created_from = filterParams.created_from
      if (filterParams.created_to) params.created_to = filterParams.created_to
      if (filterParams.keywords) params.keywords = filterParams.keywords
    }

    return this.api.downloadFile(`${modelConfig.path}/export`, 'employee_export.xlsx', { params })
  }

  static downloadSampleImportEmployee(company_id?: number) {
    const params = company_id ? { company_id } : {}

    return this.api.downloadFile(`/employee/download`, 'employee_import_sample.xlsx', { params })
  }

  // static mappingPhone(payload: IMappingPhonePayload) {

  static mappingPhone(payload: IMappingPhonePayload) {
    return this.api.put<IResponse<IUser>>({
      url: '/employee/mapping-phone',
      data: payload
    })
  }

  static submitDeleteUser(id: number, name: string) {
    return this.api.put<IResponse<IUser[]>>({
      url: `/users/${id}/submit-request-delete`,
      data: {
        action: name
      }
    })
  }

  static countUsser() {
    return this.api.get({
      url: '/users/count-user'
    })
  }
}

User.setup(modelConfig)

export function useCreateUserMutation() {
  return useMutation<IResponse<IUser>, IHttpError, ICreateUserPayload>({
    mutationFn: payload => {
      return User.createUser(payload).then(r => r.data)
    }
  })
}

export function useCreateEmployeeMutation() {
  return useMutation<IResponse<IUser>, IHttpError, ICreateUserPayload>({
    mutationFn: payload => {
      return User.createEmployee(payload).then(r => r.data)
    }
  })
}

export function useUpdateEmployeeMutation() {
  return useMutation<IResponse<IUser>, IHttpError, { id: number; data: Partial<IUser> }>({
    mutationFn: ({ id, data }) => {
      return User.updateEmployee(id, data).then(r => r.data)
    }
  })
}

export function useUpdateUserStatusMutation() {
  return useMutation<
    IResponse<IUser[]>,
    IHttpError,
    { id: number[]; status: UserStatusEnum; note?: { message: string }[] }
  >({
    mutationFn: ({ id, status, note }) => {
      return User.updateStatus(id, status, note).then(r => r.data)
    }
  })
}

export function useEmployeeImportMutation() {
  return useMutation<IResponse<IImportResponse>, IHttpError, { data: any }>({
    mutationFn: ({ data }) => {
      return User.importEmployee(data).then(r => r.data)
    }
  })
}

export function useEmployeeImportDeleteMutation() {
  return useMutation<IResponse<IImportResponseDelete>, IHttpError, { data: any }>({
    mutationFn: ({ data }) => {
      return User.importEmployeeDelete(data).then(r => r.data)
    }
  })
}

// export function useExportEmployeeMutation() {
//   return useMutation<void, IHttpError, number[] | undefined>({
//     mutationFn: userIds => {
//       return User.exportEmployee(userIds)
//     }
//   })
// }

export function useDownloadSampleImportEmployee() {
  return useMutation<void, IHttpError, number | undefined>({
    mutationFn: company_id => {
      return User.downloadSampleImportEmployee(company_id)
    }
  })
}

export function useUserStatusOptions() {
  const { __ } = useTranslator()

  return [
    { value: UserStatusEnum.ACTIVE, label: __('Active') },
    { value: UserStatusEnum.DISABLE, label: __('Disable') },

    { value: UserStatusEnum.BLOCKED, label: __('Blocked') },
    { value: UserStatusEnum.WAITING, label: __('Pending') },
    // { value: UserStatusEnum.INVITED, label: __('Invited') },
    { value: UserStatusEnum.GUEST, label: __('Guest') },
    { value: UserStatusEnum.GUEST_WAITING, label: __('Guest Waiting') },
    { value: UserStatusEnum.WAITING_DELETE, label: __('Waiting For Deletion') }
  ]
}

export function useUserStatusColors() {
  return {
    [UserStatusEnum.BLOCKED]: 'error',
    [UserStatusEnum.INACTIVE]: 'secondary',
    [UserStatusEnum.DISABLE]: 'secondary',
    [UserStatusEnum.ACTIVE]: 'success',
    [UserStatusEnum.WAITING]: 'warning',
    [UserStatusEnum.DELETED]: 'error',
    // [UserStatusEnum.INVITED]: 'info',
    [UserStatusEnum.GUEST]: 'primary',
    [UserStatusEnum.GUEST_WAITING]: 'primary',
    [UserStatusEnum.WAITING_DELETE]: 'error'
  } as const
}

export function useAssignAgentMutation() {
  return useMutation<IResponse<IUser>, IHttpError, { user_id: number; agent_id: number }>({
    mutationFn: payload => {
      return User.assignAgent(payload).then(r => r.data)
    }
  })
}

export function usePromoteUserMutation() {
  return useMutation<IResponse<IUser>, IHttpError, number>({
    mutationFn: id => {
      return User.promoteUser(id).then(r => r.data)
    }
  })
}

export function useUpdateAvatarMutation() {
  return useMutation<IResponse<IUser>, IHttpError, { id: number; avatar: File }>({
    mutationFn: ({ id, avatar }) => {
      return User.updateAvatar(id, avatar).then(r => r.data)
    }
  })
}

export function useChangePermissionMutation() {
  return useMutation<IResponse<IUser>, IHttpError, IChangePermissionUserPayload>({
    mutationFn: payload => {
      return User.changePermissionUser(payload).then(r => r.data)
    }
  })
}

export function useToggleShowPointMutation() {
  return useMutation<IResponse<IUser[]>, IHttpError, { id: number; show: boolean }>({
    mutationFn: ({ id, show }) => {
      return User.toggleShowPoint(id, show).then(r => r.data)
    }
  })
}

export function useResendInviteUserMutation() {
  return useMutation<IResponse<IUser>, IHttpError, number>({
    mutationFn: id => {
      return User.resendInviteUser(id).then(r => r.data)
    }
  })
}

export function useExportEmployeeMutation() {
  return useMutation<void, IHttpError, { userIds?: number[]; filterParams?: Record<string, string | undefined> } | undefined>({
    mutationFn: params => {
      if (!params) {
        return User.exportEmployee(undefined, undefined)
      }
      return User.exportEmployee(params.userIds, params.filterParams)
    }
  })
}

export function useChannelOptions() {
  const { __ } = useTranslator()

  return [
    { value: NotificationChannel.Email, label: __('Email') },
    { value: NotificationChannel.Telegram, label: __('Telegram') },
    { value: NotificationChannel.Whatsapp, label: __('Whatsapp') }
  ]
}

export function useEmailChannel() {
  const { __ } = useTranslator()

  return [{ value: NotificationChannel.Email, label: __('Email') }]
}

export const passwordStrengthColors = ['#d32f2f', '#f57c00', '#fbc02d', '#388e3c']

export function getPasswordStrength(password: string) {
  let score = 0

  if (password?.length >= 6) score++
  if (/[0-9]/.test(password)) score++
  if (/[a-z]/.test(password)) score++
  // if (/[A-Z]/.test(password)) score++
  if (/^\S+$/.test(password)) score++
  // if (/[^A-Za-z0-9]/.test(password)) score++

  return score
}

// export function mappingPhoneNumber
export function useMappingPhoneNumberMutation() {
  return useMutation<IResponse<IUser>, IHttpError, IMappingPhonePayload>({
    mutationFn: payload => {
      return User.mappingPhone(payload).then(r => r.data)
    }
  })
}

export function useSubmitDeleteUserMutation() {
  return useMutation<IResponse<IUser[]>, IHttpError, { id: number; name: string }>({
    mutationFn: ({ id, name }) => {
      return User.submitDeleteUser(id, name).then(r => r.data)
    }
  })
}

export function useCountUserMutation() {
  return useMutation({
    mutationFn: () => {
      return User.countUsser().then(r => r.data)
    }
  })
}
